/**
 * Smart Local Chatbot Engine
 * Processes user queries using pattern matching and knowledge base
 * No external API calls required
 */

import projectsData from "../data/projectsData";

// ============================================================================
// INTENT & KEYWORD PATTERNS
// ============================================================================

const INTENTS = {
  GREETING: {
    patterns: ["hello", "hi", "hey", "greetings", "what's up"],
    responses: [
      "Hey there! 👋 I'm your AI portfolio assistant. Ask me about projects, skills, experience, or anything else from the portfolio!",
      "Hello! 😊 I'm here to help. What would you like to know about the portfolio?",
      "Hi! Ready to learn more about the projects and experience? Just ask!",
    ],
  },
  PROJECTS: {
    patterns: [
      "project",
      "projects",
      "build",
      "built",
      "work",
      "portfolio",
      "created",
      "developed",
    ],
    responses: [],
  },
  SKILLS: {
    patterns: [
      "skill",
      "skills",
      "know",
      "expertise",
      "proficient",
      "languages",
      "technology",
      "tech",
      "stack",
    ],
    responses: [],
  },
  EXPERIENCE: {
    patterns: [
      "experience",
      "job",
      "work",
      "internship",
      "career",
      "employment",
      "company",
      "professional background",
      "work background",
      "career background",
    ],
    responses: [],
  },
  EDUCATION: {
    patterns: [
      "education",
      "studied",
      "degree",
      "university",
      "college",
      "school",
      "qualification",
      "educational background",
      "academic background",
      "alma mater",
    ],
    responses: [],
  },
  CONTACT: {
    patterns: [
      "contact",
      "email",
      "phone",
      "reach",
      "connect",
      "linkedin",
      "github",
      "twitter",
      "location",
    ],
    responses: [],
  },
  TECH_SPECIFIC: {
    patterns: [
      "react",
      "node",
      "python",
      "javascript",
      "tensorflow",
      "nlp",
      "machine learning",
      "deep learning",
      "mongodb",
      "postgres",
      "fastapi",
      "next.js",
      "nextjs",
      "google colab",
      "colab",
    ],
    responses: [],
  },
  CAPABILITIES: {
    patterns: [
      "can you",
      "what can",
      "able to",
      "capable",
      "help with",
      "support",
    ],
    responses: [],
  },
  ABOUT: {
    patterns: [
      "tell me about",
      "about you",
      "who are you",
      "introduce",
      "bio",
      "tell about yourself",
    ],
    responses: [],
  },
  HIRING: {
    patterns: [
      "hire",
      "hiring",
      "why hire",
      "why should",
      "interview",
      "recruiter",
      "recruitment",
      "job opportunity",
      "position",
      "candidate",
      "why you",
      "strengths",
      "value",
    ],
    responses: [],
  },
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Normalize text for comparison
 */
const normalize = (text) => {
  return String(text ?? "")
    .toLowerCase()
    .trim()
    .replace(/[^\w\s]/g, "");
};

/**
 * Extract keywords from query
 */
const extractKeywords = (query) => {
  const normalized = normalize(query);
  const stopWords = new Set([
    "the",
    "a",
    "an",
    "and",
    "or",
    "but",
    "in",
    "on",
    "at",
    "to",
    "for",
    "of",
    "is",
    "are",
    "was",
    "were",
    "be",
    "been",
    "i",
    "you",
    "we",
    "they",
    "my",
    "your",
    "what",
    "which",
    "how",
    "why",
  ]);

  return normalized
    .split(/\s+/)
    .filter((word) => word.length > 2 && !stopWords.has(word));
};

/**
 * Detect user intent from query
 */
const detectIntent = (query) => {
  const keywords = extractKeywords(query);
  const normalizedQuery = normalize(query);

  const scores = {};

  for (const [intent, config] of Object.entries(INTENTS)) {
    let score = 0;

    // Check for pattern matches
    for (const pattern of config.patterns) {
      if (normalizedQuery.includes(normalize(pattern))) {
        score += 2;
      }
    }

    // Check for keyword matches
    for (const keyword of keywords) {
      for (const pattern of config.patterns) {
        if (
          normalize(pattern).includes(keyword) ||
          keyword.includes(normalize(pattern))
        ) {
          score += 1;
        }
      }
    }

    if (score > 0) {
      scores[intent] = score;
    }
  }

  return Object.keys(scores).length > 0
    ? Object.keys(scores).reduce((a, b) => (scores[a] > scores[b] ? a : b))
    : null;
};

// ============================================================================
// RESPONSE GENERATORS FOR EACH INTENT
// ============================================================================

/**
 * Generate greeting response
 */
const generateGreeting = () => {
  const responses = INTENTS.GREETING.responses;
  return responses[Math.floor(Math.random() * responses.length)];
};

/**
 * Generate project-related response
 */
const generateProjectResponse = (query) => {
  const keywords = extractKeywords(query);
  const normalizedQuery = normalize(query);

  // List of technology keywords for more specific filtering
  const techKeywords = [
    "react",
    "node",
    "python",
    "javascript",
    "tensorflow",
    "nlp",
    "machine",
    "learning",
    "deep",
    "learning",
    "mongodb",
    "postgres",
    "flask",
    "django",
    "express",
    "next",
    "vue",
    "angular",
    "aws",
    "firebase",
    "docker",
    "kubernetes",
    "java",
    "cpp",
    "golang",
    "fastapi",
    "colab",
  ];

  // Filter projects based on keywords
  let relevantProjects = projectsData.projects;

  // Category filtering
  if (
    normalizedQuery.includes("ai") ||
    normalizedQuery.includes("ml") ||
    normalizedQuery.includes("machine")
  ) {
    relevantProjects = projectsData.projects.filter(
      (p) => p.category === "AI ML",
    );
  } else if (
    normalizedQuery.includes("web") ||
    normalizedQuery.includes("react") ||
    normalizedQuery.includes("frontend")
  ) {
    relevantProjects = projectsData.projects.filter(
      (p) => p.category === "Web App",
    );
  }

  // Only do keyword-based filtering if tech keywords are present
  const hasTechKeyword = keywords.some((keyword) =>
    techKeywords.some(
      (tech) => tech.includes(keyword) || keyword.includes(tech),
    ),
  );

  if (hasTechKeyword) {
    for (const keyword of keywords) {
      const filtered = relevantProjects.filter((p) => {
        const projectText =
          normalize(p.title) +
          " " +
          normalize(p.description) +
          " " +
          normalize(p.technologies.join(" "));
        return projectText.includes(keyword);
      });

      if (filtered.length > 0) {
        relevantProjects = filtered;
        break;
      }
    }
  }

  if (relevantProjects.length === 0) {
    return "I have several interesting projects! You can ask about specific categories like AI/ML projects or web applications. What interests you?";
  }

  // Show top 5 projects (or all if less than 5)
  const topProjects = relevantProjects.slice(0, 5);

  let response = `Here are ${topProjects.length === 1 ? "the" : "my top"} project${topProjects.length > 1 ? "s" : ""}:\n\n`;

  topProjects.forEach((project, index) => {
    response += `${index + 1}. ${project.title} (${project.category})\n`;
    response += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
    response += `${project.description}\n\n`;
    response += `🔧 Tech: ${project.technologies.join(", ")}\n`;
    if (project.liveLink && project.liveLink !== project.github) {
      response += `🌐 Live: ${project.liveLink}\n`;
    }
    response += `💻 GitHub: ${project.github}\n\n`;
  });

  return response;
};

/**
 * Generate skills response
 */
const generateSkillsResponse = (query) => {
  const normalizedQuery = normalize(query);

  let categoryFilter = null;

  // Detect specific skill category
  if (
    normalizedQuery.includes("language") ||
    normalizedQuery.includes("programming")
  ) {
    categoryFilter = "Languages";
  } else if (
    normalizedQuery.includes("frontend") ||
    normalizedQuery.includes("react")
  ) {
    categoryFilter = "Frontend";
  } else if (
    normalizedQuery.includes("backend") ||
    normalizedQuery.includes("node")
  ) {
    categoryFilter = "Backend";
  } else if (
    normalizedQuery.includes("ai") ||
    normalizedQuery.includes("ml") ||
    normalizedQuery.includes("machine")
  ) {
    categoryFilter = "AI/ML";
  } else if (
    normalizedQuery.includes("tool") ||
    normalizedQuery.includes("devops")
  ) {
    categoryFilter = "Tools & DevOps";
  }

  let skills = projectsData.allSkills;

  if (categoryFilter) {
    const categorySkills = skills.filter((s) => s.category === categoryFilter);
    if (categorySkills.length > 0) {
      return `**${categoryFilter} Skills:**\n${categorySkills.map((s) => s.name).join(", ")}`;
    }
  }

  // Return all skills grouped by category
  const groupedSkills = {};
  projectsData.allSkills.forEach((skill) => {
    if (!groupedSkills[skill.category]) {
      groupedSkills[skill.category] = [];
    }
    groupedSkills[skill.category].push(skill.name);
  });

  let response = "Here are my skills:\n\n";
  for (const [category, skillList] of Object.entries(groupedSkills)) {
    response += `**${category}:** ${skillList.join(", ")}\n`;
  }

  return response;
};

/**
 * Generate experience response
 */
const generateExperienceResponse = () => {
  if (!projectsData.experience || projectsData.experience.length === 0) {
    return "I'm an MCA graduate from NIT Warangal and currently working on exciting projects. Ask me about my projects or skills!";
  }

  let response = "**Experience:**\n\n";

  projectsData.experience.forEach((exp) => {
    response += `📍 ${exp.title} at ${exp.company}\n`;
    response += `   ${exp.month} ${exp.year}: ${exp.description}\n\n`;
  });

  return response;
};

/**
 * Generate education response
 */
const generateEducationResponse = () => {
  if (!projectsData.education || projectsData.education.length === 0) {
    return "I don't have education information available.";
  }

  let response = "**Education:**\n\n";

  const sortedEducation = [...projectsData.education].sort((a, b) => {
    const yearA = parseInt(a.year);
    const yearB = parseInt(b.year);
    return yearB - yearA; // Most recent first
  });

  sortedEducation.forEach((edu) => {
    response += `📚 ${edu.degree}\n`;
    response += `   ${edu.institution} (${edu.month} ${edu.year})\n`;
    if (edu.description) {
      response += `   ${edu.description}\n`;
    }
    response += "\n";
  });

  return response;
};

/**
 * Generate contact response
 */
const generateContactResponse = () => {
  let response = "**Contact & Social Links:**\n\n";

  if (projectsData.contact) {
    if (projectsData.contact.email) {
      response += `📧 Email: ${projectsData.contact.email}\n`;
    }
    if (projectsData.contact.location) {
      response += `📍 Location: ${projectsData.contact.location}\n`;
    }
    if (projectsData.contact.website) {
      response += `🌐 Website: ${projectsData.contact.website}\n`;
    }
  }

  if (projectsData.social) {
    response += "\n**Social Media:**\n";
    if (projectsData.social.github) {
      response += `🐙 GitHub: ${projectsData.social.github}\n`;
    }
    if (projectsData.social.linkedin) {
      response += `💼 LinkedIn: ${projectsData.social.linkedin}\n`;
    }
    if (projectsData.social.twitter) {
      response += `🐦 Twitter: ${projectsData.social.twitter}\n`;
    }
  }

  return response;
};

/**
 * Generate technology-specific response
 */
const generateTechResponse = (query) => {
  const normalizedQuery = normalize(query);
  const keywords = extractKeywords(query);

  // Find projects using this technology
  let relevantProjects = projectsData.projects.filter((p) => {
    const techText = normalize(p.technologies.join(" "));
    for (const keyword of keywords) {
      if (techText.includes(keyword)) return true;
    }
    return normalizedQuery.split(/\s+/).some((word) => techText.includes(word));
  });

  if (relevantProjects.length === 0) {
    return `I'm familiar with that technology! Check out my projects where I've used it. Ask me about specific projects or what I can build with it!`;
  }

  let response = `I've used ${extractKeywords(query)[0].toUpperCase()} in these projects:\n\n`;

  relevantProjects.forEach((project, index) => {
    response += `${index + 1}. ${project.title} - ${project.description}\n`;
  });

  return response;
};

/**
 * Generate capabilities response
 */
const generateCapabilitiesResponse = () => {
  return `I can help you learn about:\n
• 🚀 **Projects** - My portfolio projects and their details
• 💻 **Skills** - Languages, frameworks, and technologies
• 👨‍💼 **Experience** - Professional background
• 🎓 **Education** - Academic background
• 📧 **Contact** - How to reach me
• 🔧 **Technologies** - Deep dives into specific techs I use

Just ask me anything about these topics!`;
};

/**
 * Generate about/intro response
 */
const generateAboutResponse = () => {
  const profile = projectsData.profile || {};

  let response = `Hi, I'm **${profile.name || "Shantanu"}**! 👋\n\n`;

  if (profile.title) {
    response += `**${profile.title}**\n\n`;
  }

  if (profile.bio) {
    response += `${profile.bio}\n\n`;
  }

  response += `I specialize in:\n`;
  response += `• Full Stack Development (React, Angular, Node.js, Python, etc)\n`;
  response += `• AI/ML & Deep Learning (TensorFlow, PyTorch, etc)\n`;
  response += `• Docker, Git, GitLabs, etc\n\n`;

  response += `Feel free to ask about my projects, skills, or how to reach me!`;

  return response;
};

/**
 * Generate hiring/recruitment response
 */
const generateHiringResponse = () => {
  let response = `Why You Should Hire Me:\n`;
  response += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n\n`;

  response += `🚀 Full Stack Development Expertise\n`;
  response += `   • 5+ portfolio projects with React frontend and Node.js backend\n`;
  response += `   • Production-ready applications deployed on Vercel, AWS, Firebase\n`;
  response += `   • Real-time applications using Socket.io and PostgreSQL/MongoDB\n\n`;

  response += `🤖 AI/ML & Advanced Technologies\n`;
  response += `   • Deep learning projects (Epilepsy Detection using PyTorch, EEG analysis)\n`;
  response += `   • NLP expertise (SMS Spam Detector, Resume Analysis with transformers)\n`;
  response += `   • Machine Learning models (TensorFlow, Scikit-learn, ensemble methods)\n\n`;

  response += `💼 Proven Problem-Solving Skills\n`;
  response += `   • Built intelligent chatbots and recommendation systems\n`;
  response += `   • Created food donation platform (11M+ lives impacted concept)\n`;
  response += `   • Developed portfolio management with ML-based insights\n\n`;

  response += `📚 Strong Educational Background\n`;
  response += `   • MCA (Master of Computer Applications) from NIT Warangal\n`;
  response += `   • Currently working as Associate Software Engineer at BDB.ai\n`;
  response += `   • Proficient in modern tech stack and best practices\n\n`;

  response += `⚡ Quick Facts\n`;
  response += `   • 11 live projects in production\n`;
  response += `   • Experience with React, Node.js, Python, FastAPI, and more\n`;
  response += `   • Strong GitHub portfolio: github.com/sh78ba\n`;
  response += `   • Available for full-time, contract, or freelance work\n\n`;

  response += `📧 Ready to Chat?\n`;
  response += `   Let's discuss how I can contribute to your team!\n`;
  response += `   Email: sbasumatary.dev@gmail.com\n`;
  response += `   Location: Bengaluru, India`;

  return response;
};

/**
 * Find and return details about a specific project by name
 */
const findProjectByName = (query) => {
  const normalizedQuery = normalize(query);
  const firstQueryWord = normalizedQuery.split(/\s+/)[0] || "";

  // Find project that matches the query
  const matchedProject = projectsData.projects.find((project) => {
    const projectName = normalize(project.title);
    const projectNameAlt = normalize(project.name);

    return (
      normalizedQuery.includes(projectName) ||
      normalizedQuery.includes(projectNameAlt) ||
      (firstQueryWord && projectName.includes(firstQueryWord)) ||
      (firstQueryWord && projectNameAlt.includes(firstQueryWord))
    );
  });

  return matchedProject || null;
};

/**
 * Generate detailed response for a specific project
 */
const generateProjectDetailResponse = (project) => {
  let response = `📌 ${project.title} (${project.category})\n`;
  response += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
  response += `\n${project.description}\n\n`;
  response += `🔧 Technologies Used:\n`;
  project.technologies.forEach((tech) => {
    response += `   • ${tech}\n`;
  });
  response += `\n`;
  if (project.liveLink && project.liveLink !== project.github) {
    response += `🌐 Live Demo: ${project.liveLink}\n`;
  }
  response += `💻 GitHub Repository: ${project.github}\n`;

  return response;
};

/**
 * Generate a fallback response for unmatched queries
 */
const generateDefaultResponse = (query) => {
  const responses = [
    "That's an interesting question! I'm focused on topics related to my portfolio, projects, and skills. Could you ask me about those instead?",
    "I'm not sure about that one, but I'd love to tell you about my projects and experience! What would you like to know?",
    "I'm specifically trained on my portfolio content. Ask me about projects, skills, education, or how to contact me!",
    "That's outside my expertise, but I'm great at talking about what I've built! 😊 Want to know about any projects?",
  ];

  return responses[Math.floor(Math.random() * responses.length)];
};

// ============================================================================
// MAIN CHATBOT ENGINE
// ============================================================================

/**
 * Process user query and generate intelligent response
 */
export const processQuery = (query) => {
  try {
    if (!query || query.trim().length === 0) {
      return "Please ask me something! 😊";
    }

    const specificProject = findProjectByName(query);
    if (specificProject) {
      return generateProjectDetailResponse(specificProject);
    }

    const intent = detectIntent(query);
    let response = "";

    switch (intent) {
      case "GREETING":
        response = generateGreeting();
        break;
      case "PROJECTS":
        response = generateProjectResponse(query);
        break;
      case "SKILLS":
        response = generateSkillsResponse(query);
        break;
      case "EXPERIENCE":
        response = generateExperienceResponse();
        break;
      case "EDUCATION":
        response = generateEducationResponse();
        break;
      case "CONTACT":
        response = generateContactResponse();
        break;
      case "TECH_SPECIFIC":
        response = generateTechResponse(query);
        break;
      case "CAPABILITIES":
        response = generateCapabilitiesResponse();
        break;
      case "ABOUT":
        response = generateAboutResponse();
        break;
      case "HIRING":
        response = generateHiringResponse();
        break;
      default:
        response = generateDefaultResponse(query);
    }

    return response;
  } catch (error) {
    return "I ran into a temporary issue. Please try asking again in a different way.";
  }
};

/**
 * Get a random greeting message
 */
export const getRandomGreeting = () => {
  const greetings = [
    "Hey! I'm an AI assistant here to help. Ask me anything about the projects, skills, and experience showcased here 🚀",
    "Welcome! I'm your portfolio assistant. Ask me about projects, skills, or education! 😊",
    "Hello! Ready to explore the portfolio? Ask me anything! 💼",
  ];

  return greetings[Math.floor(Math.random() * greetings.length)];
};

const chatbotEngine = { processQuery, getRandomGreeting };

export default chatbotEngine;
